import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Textarea, ModalInfo } from 'shared/components';
import * as helpers from 'features/irregularities/constants';
import { PermissionsService } from 'shared/services';
import { isEmpty } from 'lodash';

const modalStyles = {
  content: {
    top: '64px',
    width: '600px',
  },
};

const mapScope = (scope) => helpers.involvedList.find((elem) => elem.value === scope).label;
const mapStages = (stages, existentStages) =>
  stages.map((stage, idx) => {
    const existent = existentStages.find((existentStage) => existentStage.id === stage.stageId);
    return (
      <span key={`stage-${idx}`}>
        {existent.name}
        {idx < stages.length - 1 ? ', ' : ''}
      </span>
    );
  });
const mapRooms = (irregRooms, rooms) => {
  if (rooms.length <= 1 && irregRooms.length >= rooms.length) {
    const room = rooms[0];
    return irregRooms.some((r) => r.roomId === room.id) ? <span>{room.roomName}</span> : '';
  } else {
    return irregRooms.map((irregRoom, idx) => {
      const room = rooms.find((r) => r.id === irregRoom.roomId);
      return (
        <span key={`room-${idx}`}>
          {room.roomName}
          {idx < irregRooms.length - 1 ? ', ' : ''}
        </span>
      );
    });
  }
};

const canApprove = (irreg, tcEvent) =>
  !irreg.reviewCompleteFlag &&
  (PermissionsService.hasTestCenterManage(tcEvent.testCenterCycleId) ||
    PermissionsService.hasEventManage(tcEvent.id));

const IrregularityInfo = (props) => {
  const { t } = useTranslation();
  const { irregularity, hideMatchIdFlag } = props;

  const modalHeader =
    irregularity && irregularity.reviewCompleteFlag
      ? 'irregularities.reviewedLogTitle'
      : 'irregularities.submittedLogTitle';
  const buttonLabel = canApprove(irregularity, props.tcEvent)
    ? 'irregularities.reviewAndApproveLabel'
    : 'common.labels.close';

  return (
    <ModalInfo
      className="irregularity-modal"
      modalIsOpen={props.modalOpen}
      closeModal={props.closeModal}
      onSubmit={props.onSubmitReviewed}
      headerTitle={modalHeader}
      saveLabel={buttonLabel}
      customStyles={modalStyles}>
      <div className="irregularity-info-container">
        <div className="pt-2 pb-3">
          <h4>{irregularity.irregularityType.irregularityCategory.name}</h4>
          <p>
            <span>
              {moment(irregularity.submitDateTime).format('hh:mm A')} -{' '}
              {moment(irregularity.submitDateTime).format('MM/DD/YYYY')}
            </span>
            {irregularity.submitPersonnel && !isEmpty(irregularity.submitPersonnel) && (
              <span className="d-block">
                {irregularity.submitPersonnel.lastName}, {irregularity.submitPersonnel.firstName}
              </span>
            )}
            {irregularity.reviewCompleteFlag ? (
              <span className="active-color d-block">{t('irregularities.reviewedLabel')}</span>
            ) : (
              <span className="in-active-color d-block">{t('irregularities.submittedLabel')}</span>
            )}
          </p>
        </div>
        <div className="pt-4 border-top-gray">
          <h5 className="mb-4">{t('irregularities.detailsTitle')}</h5>
          <p>
            <span className="text-strong d-block">{t('irregularities.timeLogTitle')}</span>
            <span>
              {moment(irregularity.startDateTime).format('hh:mm A')} for {irregularity.duration}{' '}
              minutes
            </span>
          </p>
          <p>
            <span className="text-strong d-block">{t('irregularities.stageMessage')}</span>
            {!!irregularity.irregularityStages.length &&
              !!props.irregularityStages.length &&
              mapStages(irregularity.irregularityStages, props.irregularityStages)}
          </p>
          <p>
            <span className="text-strong d-block">{t('irregularities.involvementMessage')}</span>
            <span>{mapScope(irregularity.scope)}</span>
          </p>
          {!!irregularity.irregularityRooms.length && props.allRoomsDTO.length && (
            <p>
              <span className="text-strong d-block">{t('common.labels.location')}</span>
              {mapRooms(irregularity.irregularityRooms, props.allRoomsDTO)}
            </p>
          )}
          {!!irregularity.irregularityExaminees.length && (
            <div>
              <span className="text-strong d-block">
                {t('irregularities.examineeInvolvedLabel')}
              </span>
              {irregularity.irregularityExaminees.map((examinee) => (
                <p key={examinee.id}>
                  <span className="d-block">
                    {' '}
                    {examinee.lastName}, {examinee.firstName}
                  </span>
                  {!hideMatchIdFlag && (
                    <span className="d-block font-size-12">
                      {' '}
                      {t('irregularities.matchIDLabel')}:{' '}
                      {examinee.matchId || t('common.labels.notSpecified')}{' '}
                    </span>
                  )}
                  <span className="d-block font-size-12">
                    {' '}
                    {t('irregularities.voidDocumentsLabel')}:{' '}
                    {t(examinee.voidDocumentFlag ? 'common.labels.yes' : 'common.labels.no')}
                  </span>
                  <span className="d-block font-size-12">
                    {' '}
                    {t('irregularities.examineeNotifiedLabel')}:{' '}
                    {t(examinee.examineeNotifiedFlag ? 'common.labels.yes' : 'common.labels.no')}
                  </span>
                </p>
              ))}
            </div>
          )}
          <p>
            <span className="text-strong d-block">{t('common.labels.notes')}</span>
            <span>{irregularity.notes || t('common.labels.notSpecified')}</span>
          </p>
          {irregularity.reviewCompleteFlag && irregularity.reviewNotes && (
            <p>
              <span className="text-strong d-block">{t('irregularities.feedbackSubtitle')}</span>
              <span>{irregularity.reviewNotes}</span>
            </p>
          )}
        </div>
        {canApprove(irregularity, props.tcEvent) && (
          <div className="pt-4 border-top-gray">
            <h5 className="mb-4">{t('irregularities.feedbackSubtitle')}</h5>
            {irregularity.reviewCompleteFlag ? (
              <p>{irregularity.reviewNotes}</p>
            ) : (
              <div>
                <Textarea
                  name="reviewNotes"
                  value={irregularity.reviewNotes || ''}
                  onChange={props.onChange}
                  maxLength={300}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </ModalInfo>
  );
};

IrregularityInfo.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  hideMatchIdFlag: PropTypes.bool,
  irregularity: PropTypes.shape({
    reviewCompleteFlag: PropTypes.bool,
  }).isRequired,
  irregularityStages: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onSubmitReviewed: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  tcEvent: PropTypes.shape({}).isRequired,
};

export default IrregularityInfo;
